import { Injectable } from '@angular/core';
import { BaseApiHandleOptions, BaseApiService } from './base-api.service';
import { Observable } from 'rxjs';
import {
    VisitorIntelCompanyDto,
    VisitorIntelMigrationStatusResponseDto,
    VisitorIntelResponseInterface,
    VisitorIntelLastGeneratedDateDto,
    ExportErrorsResponseApiDto,
    AutomationErrorsResponseApiDto,
    VisitorIntelReportResponseInterface,
    RunIntentAutomationDto,
    MappingFieldsRequestParamDto,
} from '../../dto';
import { CRMCurrentlyMappedFieldsResponse, CrmFieldsResponseDto } from '../../dto';
import { VisitorIntelProviderEnum } from '../../enums';
import { SavedAutomationInterface } from '../../models';
import { shareReplay } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class VisitorIntelApiService {
    private readonly url: string = 'service/contactcloud/visitorintel';
    private migrationStatusCache = new Map<string, Observable<VisitorIntelMigrationStatusResponseDto>>();

    constructor(private readonly baseApiService: BaseApiService) {}

    getMigrationStatus(uuId: string): Observable<VisitorIntelMigrationStatusResponseDto> {
        if (this.migrationStatusCache.has(uuId)) {
            return this.migrationStatusCache.get(uuId)!;
        }
    
        const observable = this.baseApiService.get<VisitorIntelMigrationStatusResponseDto>(`${this.url}/migration/get-status`, {
            params: { uuId },
            showError: true,
        }).pipe(
            shareReplay(1)
        );

        this.migrationStatusCache.set(uuId, observable);
        return observable;
    }

    getScript(accountId: number): Observable<{ script: string }> {
        return this.baseApiService.get(`${this.url}/script/get`, {
            params: { accountId },
            showError: true,
        });
    }

    startMigration(uuId: string, options?: BaseApiHandleOptions): Observable<null> {
        return this.baseApiService.post(`${this.url}/migration/start`, null, { ...options, params: { uuId } });
    }

    getVisitorIntel(request: any, provider: VisitorIntelProviderEnum = VisitorIntelProviderEnum.BOMBORA): Observable<VisitorIntelResponseInterface> {
        return this.baseApiService.post(
            `${this.url}/filter`,
            request,
            { params: { provider } }
        );
    }

    getCompanies(request: any, provider: VisitorIntelProviderEnum = VisitorIntelProviderEnum.BOMBORA): Observable<VisitorIntelCompanyDto[]> {
        return this.baseApiService.post(
            `${this.url}/companies`,
            request,
            { params: { provider }, showError: true }
        );
    }

    getVisitorIntelExport(request: any, provider: VisitorIntelProviderEnum = VisitorIntelProviderEnum.BOMBORA): Observable<any> {
        return this.baseApiService.post(
            `${this.url}/export`,
            request,
            { params: { provider } },
            {
                responseType: 'text',
                observe: 'response',
            });
    }

    getVILastGeneratedDate(uuId: string): Observable<VisitorIntelLastGeneratedDateDto> {
        return this.baseApiService.get(`${this.url}/last-generated-date/${uuId}`, {
            showError: true,
        });
    }

    saveIntentAutomation(newIntentObj: SavedAutomationInterface) {
        return this.baseApiService.post(`${this.url}/automations`, newIntentObj);
    }

    getVIAutomations(params: any = {}): Observable<SavedAutomationInterface[]> {
        return this.baseApiService.get(`${this.url}/automations`, { params });
    }

    deleteAutomation(automationId: number): Observable<void> {
        return this.baseApiService.delete(`${this.url}/automations/${automationId}`);
    }

    updateAutomation(automationId: number, updatedAutomation: SavedAutomationInterface): Observable<void> {
        return this.baseApiService.put(`${this.url}/automations/${automationId}`, updatedAutomation);
    }

    getCRMMappingFields(crm: string, params: MappingFieldsRequestParamDto = {}): Observable<CrmFieldsResponseDto> {
        const requestParams = params.isAutomation ? { is_automation: true } : {};
        return this.baseApiService.get<CrmFieldsResponseDto>(`service/contactcloud/${crm.toLowerCase()}/fields`, { params: requestParams });
    }

    getExportErrors(): Observable<ExportErrorsResponseApiDto | AutomationErrorsResponseApiDto[]> {
        return this.baseApiService.get<ExportErrorsResponseApiDto | AutomationErrorsResponseApiDto[]>(
            `${this.url}/automations/export/errors`
        );
    }

    getCurrentlyMappedFields(crm: string, objectType: 'COMPANY' | 'CONTACT' = 'COMPANY'): Observable<CRMCurrentlyMappedFieldsResponse> {
        return this.baseApiService.get(
          `service/contactcloud/crms/mappedFields`,
          {
            params: {
              crmType: crm.toUpperCase(),
              objectType: objectType
            }
          }
        );
    }

    getCompanyIdsForReport(intentReportId: string): Observable<VisitorIntelReportResponseInterface> {
        return this.baseApiService.get<VisitorIntelReportResponseInterface>(`service/contactcloud/visitorintel/automations/report/${intentReportId}/companyIds`);
    }

    runVisitorIntelAutomation(viObj: RunIntentAutomationDto): Observable<null> {
        return this.baseApiService.post(`service/contactcloud/visitorintel/automations/run`, viObj, { showError: true });
    }
}
